import { useCallback, useMemo, useState } from 'react'

/**
 * Discriminated payload a bulk endpoint accepts. Explicit mode sends the
 * ticked ids; select-all mode sends the filter snapshot plus the ids to
 * exclude. Keys are snake_case to match the server contract.
 */
export type BulkSelectionPayload<TFilters> =
  | { ids: string[] }
  | { select_all: true; exclude_ids: string[]; filters: TFilters }

export type HeaderCheckboxState = 'unchecked' | 'indeterminate' | 'checked'

export interface UseBulkSelectionParams<TFilters> {
  /** Row ids visible on the current page. Drives the header tri-state. */
  pageIds: string[]
  /** Current filter state. Snapshotted when select-all is engaged. */
  filters: TFilters
  /** Server count under the active filters (from the list response). */
  totalCount: number
}

export interface UseBulkSelection<TFilters> {
  /** Explicit mode: ids ticked individually. Empty in select-all mode. */
  selectedIds: Set<string>
  isSelectAll: boolean
  /** Select-all mode: ids the user has unticked from the full set. */
  excludedIds: Set<string>
  /** Effective selected count across both modes. */
  selectedCount: number
  /** Whether a given row counts as selected in the current mode. */
  isSelected: (id: string) => boolean
  /** Toggle a single row on/off in the current mode. */
  toggle: (id: string) => void
  /** Toggle every row on the current page (explicit mode). */
  togglePage: () => void
  /** Engage select-all mode, snapshotting the current filters. */
  selectAll: () => void
  /** Clear all selection state in both modes. */
  reset: () => void
  /** Serialized payload for a bulk endpoint, keyed by the active mode. */
  payload: BulkSelectionPayload<TFilters>
  /** Tri-state for the header checkbox, derived from the current page. */
  headerState: HeaderCheckboxState
}

export function useBulkSelection<TFilters = Record<string, unknown>>({
  pageIds,
  filters,
  totalCount,
}: UseBulkSelectionParams<TFilters>): UseBulkSelection<TFilters> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [isSelectAll, setIsSelectAll] = useState(false)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set())
  const [filterSnapshot, setFilterSnapshot] = useState<TFilters>(filters)

  const isSelected = useCallback(
    (id: string) => (isSelectAll ? !excludedIds.has(id) : selectedIds.has(id)),
    [isSelectAll, excludedIds, selectedIds],
  )

  const toggle = useCallback(
    (id: string) => {
      if (isSelectAll) {
        // Stay in select-all; unticking excludes, re-ticking re-includes.
        setExcludedIds((prev) => {
          const next = new Set(prev)
          if (next.has(id)) next.delete(id)
          else next.add(id)
          return next
        })
        return
      }
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    [isSelectAll],
  )

  const togglePage = useCallback(() => {
    const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => isSelected(id))
    if (isSelectAll) {
      // In select-all, clearing the page means excluding its rows; selecting
      // means re-including them.
      setExcludedIds((prev) => {
        const next = new Set(prev)
        pageIds.forEach((id) => (allOnPageSelected ? next.add(id) : next.delete(id)))
        return next
      })
      return
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      pageIds.forEach((id) => (allOnPageSelected ? next.delete(id) : next.add(id)))
      return next
    })
  }, [pageIds, isSelected, isSelectAll])

  const selectAll = useCallback(() => {
    setIsSelectAll(true)
    setExcludedIds(new Set())
    setSelectedIds(new Set())
    setFilterSnapshot(filters)
  }, [filters])

  const reset = useCallback(() => {
    setIsSelectAll(false)
    setExcludedIds(new Set())
    setSelectedIds(new Set())
  }, [])

  const selectedCount = isSelectAll ? Math.max(0, totalCount - excludedIds.size) : selectedIds.size

  const payload = useMemo<BulkSelectionPayload<TFilters>>(
    () =>
      isSelectAll
        ? { select_all: true, exclude_ids: Array.from(excludedIds), filters: filterSnapshot }
        : { ids: Array.from(selectedIds) },
    [isSelectAll, excludedIds, filterSnapshot, selectedIds],
  )

  const headerState: HeaderCheckboxState = useMemo(() => {
    if (pageIds.length === 0) return 'unchecked'
    const selectedOnPage = pageIds.filter((id) => isSelected(id)).length
    if (selectedOnPage === 0) return 'unchecked'
    if (selectedOnPage === pageIds.length) return 'checked'
    return 'indeterminate'
  }, [pageIds, isSelected])

  return {
    selectedIds,
    isSelectAll,
    excludedIds,
    selectedCount,
    isSelected,
    toggle,
    togglePage,
    selectAll,
    reset,
    payload,
    headerState,
  }
}
